// To Do filter area (docs/spec-todo-worklists.md): search, one "Filter" button with a count, and the sort. Behind the button: type and
// status as segmented controls with counts, pill dropdowns with a custom popover (no native select), whatever the page adds (extra).
// What is switched on shows as removable chips under the bar (TodoFilterChips). Styles: .tdf-* in global.css.
import { useEffect, useRef, useState } from 'react'

export function Segmented({ options, value, onChange, small, label }) {
  return (
    <div className={'tdf-seg' + (small ? ' tdf-seg-sm' : '')} role="tablist" aria-label={label}>
      {options.map(o => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={'tdf-seg-btn' + (value === o.value ? ' on' : '')} onClick={() => onChange(o.value)}>
          {o.label}{o.count != null && <span className="tdf-seg-n">{o.count}</span>}
        </button>
      ))}
    </div>
  )
}

const Chevron = () => <svg className="tdf-chev" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
const Check = () => <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2l2.4 2.4 4.6-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>

// A pill that opens a menu. value '' = not set (shows the label); set + clearable = light blue "Engineering ✕".
// prefix: "Sort:" style pills always show "prefix value" and are never blue.
export function PillSelect({ label, value, options, onChange, clearable = true, prefix, align = 'left', placeholder }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const out = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const esc = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc) }
  }, [open])
  useEffect(() => { if (!open) setQ('') }, [open])
  const sel = options.find(o => String(o.value) === String(value))
  const set = clearable && value !== '' && value != null
  const shown = q ? options.filter(o => String(o.label).toLowerCase().includes(q.toLowerCase())) : options
  return (
    <div className="tdf-pill-wrap" ref={ref}>
      <span className={'tdf-pill' + (set ? ' set' : '') + (open ? ' open' : '')}>
        <button type="button" className="tdf-pill-main" aria-haspopup="listbox" aria-expanded={open} aria-label={label + (sel && (set || prefix) ? ': ' + sel.label : '')} onClick={() => setOpen(o => !o)}>
          <span className="tdf-pill-text">{prefix ? <><span className="tdf-pill-pre">{prefix}</span> {sel ? sel.label : label}</> : set && sel ? sel.label : set ? String(value) : (placeholder || label)}</span>
          {!set && <Chevron />}
        </button>
        {set && <button type="button" className="tdf-x" aria-label={'Clear ' + label} onClick={() => { onChange(''); setOpen(false) }}>✕</button>}
      </span>
      {open && (
        <div className={'tdf-menu' + (align === 'right' ? ' right' : '')} role="listbox" aria-label={label}>
          {options.length > 8 && <input className="tdf-menu-q" autoFocus placeholder={'Find ' + label.toLowerCase() + '…'} value={q} onChange={e => setQ(e.target.value)} />}
          {shown.map(o => {
            const on = String(o.value) === String(value)
            return (
              <button key={String(o.value)} type="button" role="option" aria-selected={on} className={'tdf-opt' + (on ? ' on' : '')} onClick={() => { onChange(o.value); setOpen(false) }}>
                <span className="tdf-opt-check">{on && <Check />}</span><span className="tdf-opt-label">{o.label}</span>
              </button>
            )
          })}
          {!shown.length && <div className="tdf-empty">Nothing found</div>}
        </div>
      )}
    </div>
  )
}

// filters: { q, department, equipment, list, location, status, type }; options: { departments, components, lists, locations }
// (each [{ value, label }]); typeTabs: [{ value, label, count }]; sort: the sort pill, beside the button; extra: more filters inside the
// popover (assignee, high only; History: closed date, closed by), extraCount of them switched on; resultCount: the number on the button
// that closes the popover; hideStatus: History (closed items only) has no Status control
export default function TodoFilterBar({ filters, setFilter, clearAll, anyOn, typeTabs, options, showList, sort, extra, extraCount = 0, resultCount, hideStatus, keyHint }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const out = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const esc = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc) }
  }, [open])
  const count = ['department', 'equipment', 'location', 'type'].filter(k => filters[k]).length + (showList && filters.list ? 1 : 0) + (!hideStatus && filters.status !== 'open' ? 1 : 0) + extraCount
  return (
    <div className="tdf tdf-bar">
      <label className="tdf-search">
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        <input placeholder="Search…" aria-label="Search" value={filters.q} onChange={e => setFilter('q', e.target.value)} />
        {filters.q ? <button type="button" className="tdf-x" aria-label="Clear search" onClick={() => setFilter('q', '')}>✕</button> : keyHint && <span className="tdc-kbd">{keyHint}</span>}
      </label>
      <div className="tdf-filter-wrap" ref={ref}>
        <button type="button" className={'tdf-filter-btn' + (count ? ' on' : '')} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(v => !v)}>
          <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 4h12M4.5 8h7M7 12h2" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
          Filter{count > 0 && <span className="tdf-count">{count}</span>}
        </button>
        {open && (
          <div className="tdf-pop" role="dialog" aria-label="Filter">
            <div className="tdf-pop-head"><b>Filter</b>{anyOn && <button type="button" className="tdf-clear" onClick={clearAll}>Clear all</button>}</div>
            <div className="tdf-cap">Type</div>
            <Segmented label="Type" options={typeTabs} value={filters.type} onChange={v => setFilter('type', v)} />
            {!hideStatus && <><div className="tdf-cap">Status</div><Segmented small label="Status" options={[{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'all', label: 'All' }]} value={filters.status} onChange={v => setFilter('status', v)} /></>}
            <div className="tdf-grid">
              <div><div className="tdf-cap">Department</div><PillSelect label="Department" placeholder="Any" value={filters.department} options={options.departments} onChange={v => setFilter('department', v)} /></div>
              <div><div className="tdf-cap">Component</div><PillSelect label="Component" placeholder="Any" align="right" value={filters.equipment} options={options.components} onChange={v => setFilter('equipment', v)} /></div>
              {showList && <div><div className="tdf-cap">List</div><PillSelect label="List" placeholder="Any" value={filters.list} options={options.lists} onChange={v => setFilter('list', v)} /></div>}
              <div><div className="tdf-cap">Location</div><PillSelect label="Location" placeholder="Any" align="right" value={filters.location} options={options.locations} onChange={v => setFilter('location', v)} /></div>
              {extra}
            </div>
            <button type="button" className="tdf-show" onClick={() => setOpen(false)}>{resultCount == null ? 'Done' : 'Show ' + resultCount + ' task' + (resultCount === 1 ? '' : 's')}</button>
          </div>
        )}
      </div>
      {sort}
    </div>
  )
}

// the chips of the filters of the bar (the page and History add their own). onRemove clears that one filter.
export function filterChips(filters, setFilter, { options, showList, hideStatus }, names = {}) {
  const name = (list, v) => (list.find(o => String(o.value) === String(v)) || {}).label || String(v)
  const out = []
  if (filters.type) out.push({ key: 'type', label: 'Type', value: names.type ? names.type(filters.type) : filters.type, onRemove: () => setFilter('type', '') })
  if (!hideStatus && filters.status !== 'open') out.push({ key: 'status', label: 'Status', value: filters.status === 'closed' ? 'Closed' : 'All', onRemove: () => setFilter('status', 'open') })
  if (filters.department) out.push({ key: 'department', label: 'Department', value: filters.department, onRemove: () => setFilter('department', '') })
  if (filters.equipment) out.push({ key: 'equipment', label: 'Component', value: name(options.components, filters.equipment), onRemove: () => setFilter('equipment', '') })
  if (showList && filters.list) out.push({ key: 'list', label: 'List', value: name(options.lists, filters.list), onRemove: () => setFilter('list', '') })
  if (filters.location) out.push({ key: 'location', label: 'Location', value: filters.location, onRemove: () => setFilter('location', '') })
  return out
}
export function TodoFilterChips({ chips, clearAll }) {
  if (!chips.length) return null
  return (
    <div className="tdf-chips">
      {chips.map(c => (
        <span key={c.key} className="tdf-chip"><span className="tdf-chip-l">{c.label}</span> {c.value}
          <button type="button" aria-label={'Remove ' + c.label + ' filter'} onClick={c.onRemove}>×</button>
        </span>
      ))}
      <button type="button" className="tdf-clear" onClick={clearAll}>Clear all</button>
    </div>
  )
}
